/* global globalThis */

import { featureRegistry } from './feature-registry';

/**
 * Frontend-only loader for local bundles extracted from index.js.
 *
 * The backend continues to own third-party scripts and every
 * Model.ShouldAdd... condition in the PageBody partial.
 */
const currentScript = document.currentScript;
// Derive the release-specific asset directory from loader.js itself, so the
// backend only needs to include this one script tag.
const assetBase = currentScript && currentScript.src
  ? new URL('.', currentScript.src).href
  : '/assets/js/';
const loadedBundles = new Map();

const has = selector => document.querySelector(selector) !== null;

const loadBundle = bundle => {
  const url = `${assetBase}${bundle}`;

  if (loadedBundles.has(url)) {
    return loadedBundles.get(url);
  }

  const promise = new Promise((resolve, reject) => {
    const script = document.createElement('script');

    script.src = url;
    // Dynamically added scripts are independent feature bundles.
    script.async = true;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Unable to load ${url}`));
    document.head.appendChild(script);
  });

  loadedBundles.set(url, promise);

  return promise;
};

const logError = error => {
  if (globalThis.console) {
    globalThis.console.error(error);
  }
};

const initializeFeature = feature => loadBundle(feature.bundle)
  .then(() => {
    if (feature.initialize) {
      feature.initialize();
    }
  });

const scheduleWhenVisible = feature => {
  const target = document.querySelector(feature.selector);

  if (!target || !globalThis.IntersectionObserver) {
    initializeFeature(feature).catch(logError);

    return;
  }

  // Delay non-essential below-the-fold features without delaying core UI.
  const observer = new globalThis.IntersectionObserver(entries => {
    if (entries.some(entry => entry.isIntersecting)) {
      observer.disconnect();
      initializeFeature(feature).catch(logError);
    }
  }, { rootMargin: '300px 0px' });

  observer.observe(target);
};

for (const feature of featureRegistry) {
  if (!has(feature.selector)) {
    continue;
  }

  if (feature.priority === 'viewport') {
    scheduleWhenVisible(feature);

    continue;
  }

  initializeFeature(feature).catch(logError);
}

globalThis.HMS = globalThis.HMS || {};
globalThis.HMS.loadFeature = loadBundle;
